import { z } from 'zod';

import { BooleanStringSchema, DateStringSchema, TimeStringSchema } from '../../common';
import { FirstAvailableDateSchema } from '../../experience/sessions';
import { buildQueryString } from '../../utils/querystring';
import { PublicResponseListingAddOnSchema, ResponseListingAddOnSchema } from '../add-on/listing-add-on';
import { AuctionResponse, AuctionStatus } from '../auction/auction';
import { Currency, LanguageCode, ProductLine } from '../brand/brand';
import { HostSchema, makeEndpoint, paginateQuerySchema, paginateResponseSchema } from '../common';
import {
  BookingAvailabilityModeValue,
  ExperienceType,
  ExternalFieldSchema,
  IntegrationMetaSchema,
  ShouldValidateComplimentaryBookingSchema,
  TaxSchema,
} from '../experience/experiences';
import { LimitedTimeSchema } from '../experience/limited-time';
import { PriceTierSchema } from '../experience/price-tier-rules';
import { LocationSchema } from '../location/location';
import { ListingPictureKind } from '../media/media';
import { PaymentMethods } from '../payment-method/public';
import { PromotedListingStatus } from '../promoter/types';
import {
  CustomQuestionInResourcesSchema,
  ExperienceImageSizeMediaSchema,
  MediaSchema,
  ResourceGroupCollectionEventType,
} from '../resource/common';
import { ResourceGroupResponseSchema } from '../resource/resource-group';
import { TranslationSchema } from '../translation/types';
import { UserImageSizeMediaSchema } from '../user/user';

export enum ListingKind {
  EXPERIENCE = 'experience',
  RESOURCE = 'resource', // refers to a collection
  EVENT = 'event',
  AUCTION = 'auction',
}

export enum ListingVersion {
  PUBLISHED = 'published',
  LATEST = 'latest',
}

export const ListingPictureSchema = ExperienceImageSizeMediaSchema.extend({
  kind: z.nativeEnum(ListingPictureKind),
});

const ResourceGroupSchema = ResourceGroupResponseSchema.extend({
  firstAvailableDate: FirstAvailableDateSchema.nullable(),
});

export const ListingTaxSchema = TaxSchema.and(z.object({ name: z.string() }));

// Schema for auction response in listings with bid stats and consistent null values
const ListingAuctionResponse = AuctionResponse.extend({
  publishedStatus: z.nativeEnum(AuctionStatus).nullable(),
  winningAmount: z.string().nullable(),
  winningBidderId: z.string().uuid().nullable(),
  bidsCount: z.number(),
  uniqueBiddersCount: z.number(),
});

const ListingSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  type: z.nativeEnum(ExperienceType).nullable(),
  partySize: z.number().nullable(),
  headline: z.string(),
  vibes: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  category: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  coverPicture: ExperienceImageSizeMediaSchema.nullable(),
  firstAvailableDate: FirstAvailableDateSchema.nullable(),
  kind: z.nativeEnum(ListingKind),
  eventType: z.nativeEnum(ResourceGroupCollectionEventType).nullable(),
  productLine: z.nativeEnum(ProductLine),
  slug: z.string().nullable(),
  defaultPriceTiers: z.array(PriceTierSchema),
  conditionalPriceTiers: z.array(PriceTierSchema).nullable(),
  defaultMaxParticipantCount: z.number().nullable(),
  description: z.string(),
  location: LocationSchema.nullable(),
  isBookable: z.boolean(),
  isUnlisted: z.boolean(),
  hostedBy: HostSchema.extend({
    profilePicture: UserImageSizeMediaSchema.nullable(),
    description: z.string().nullable(),
  }).nullable(),
  isCurated: z.boolean(),
  isExclusive: z.boolean(),
  hidePrice: z.boolean(),
  bookingAvailabilityMode: z.nativeEnum(BookingAvailabilityModeValue).nullable(),
  descriptionPicture: ExperienceImageSizeMediaSchema.nullable(),
  paymentMethods: z.array(z.nativeEnum(PaymentMethods)).nullable(),
  taxes: z.array(ListingTaxSchema).nullable(),
  integrationMeta: z.array(IntegrationMetaSchema).nullable(),
  providedItems: z.array(z.string()).nullable(),
  reminder: z.string().nullable(),
  pictures: z.array(ListingPictureSchema),
  medias: z.array(
    MediaSchema.omit({
      kind: true,
    }).extend({
      kind: z.nativeEnum(ListingPictureKind),
    }),
  ),
  ratings: z.object({ rating: z.number().nullable(), recommendability: z.number().nullable(), total: z.number() }),
  minimumRequiredToBook: z.number().nonnegative().nullable(),
  maximumAllowedToBook: z.number().nonnegative().nullable(),
  externalField: ExternalFieldSchema.nullable(),
  map: ExperienceImageSizeMediaSchema.nullable(),
  resourceGroups: z
    .array(
      ResourceGroupSchema.pick({
        id: true,
        title: true,
        firstAvailableDate: true,
        paymentMethods: true,
      }),
    )
    .nullable(),
  brandId: z.string().uuid(),
  listingId: z.string().uuid().nullable().optional(),
  isFeatured: z.boolean(),
  cancellationPolicyId: z.string().uuid().nullable(),
  reschedulePolicyId: z.string().uuid().nullable(),
  promotedListingId: z.string().uuid().nullable().optional(),
  promotedListingStatus: z.nativeEnum(PromotedListingStatus).nullable().optional(),
  providerBrandId: z.string().uuid().nullable().optional(),
  providerBrandName: z.string().nullable().optional(),
  auction: ListingAuctionResponse.nullable().optional(),
  limitedTime: LimitedTimeSchema.omit({ createdAt: true }).nullable().optional(),
  customCallToAction: z.string().nullable(),
});

export const PublicListingSchema = ListingSchema.extend({
  agenda: z.string().nullable(),
  menu: z.string().nullable(),
  dates: z.array(DateStringSchema).nullable(),
  startTimes: z.array(TimeStringSchema).nullable(),
  supportedCurrencies: z.array(z.nativeEnum(Currency)),
});

export type PublicListing = z.infer<typeof PublicListingSchema>;

export const PublicDetailedListingSchema = PublicListingSchema.extend({
  resourceGroups: z
    .array(
      ResourceGroupSchema.extend({
        ratings: PublicListingSchema.shape.ratings,
        shouldValidateComplimentaryBooking: z.union([z.boolean(), ShouldValidateComplimentaryBookingSchema]).nullable(),
        addOns: z.array(PublicResponseListingAddOnSchema),
      }),
    )
    .nullable(),
  auction: ListingAuctionResponse.nullable().optional(),
  limitedTime: LimitedTimeSchema.omit({ createdAt: true }).nullable().optional(),
  shouldValidateComplimentaryBooking: z.union([z.boolean(), ShouldValidateComplimentaryBookingSchema]).nullable(),
  waitlistId: z.string().uuid().nullable(),
  addOns: z.array(PublicResponseListingAddOnSchema),
  customQuestions: z.array(CustomQuestionInResourcesSchema).nullable(),
});

export type PublicDetailedListing = z.infer<typeof PublicDetailedListingSchema>;

export const PublicGetListingsQuerySchema = paginateQuerySchema({ sortBy: ['title', 'kind', 'date', 'order'] as const })
  .omit({ q: true })
  .extend({
    categoryIds: z.array(z.string().uuid()).optional(),
    vibeIds: z.array(z.string().uuid()).optional(),
    kinds: z.array(z.nativeEnum(ListingKind)).optional(),
    brandIds: z.array(z.string().uuid()).optional(),
    search: z.string().optional(),
    isUnlisted: BooleanStringSchema.default('false'),
    version: z.nativeEnum(ListingVersion).optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    featured: z.literal('true').optional(),
    bypassFirstAvailableDate: BooleanStringSchema.optional(),
    language: z.nativeEnum(LanguageCode).optional(),
  });

export const PublicGetListingsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ query }: { query: z.input<typeof PublicGetListingsQuerySchema> }) => `/v1/listings?${buildQueryString(query)}`,
  requestSchema: z.void(),
  responseSchema: paginateResponseSchema(PublicListingSchema.strict()),
  querySchema: PublicGetListingsQuerySchema,
});

const PublicGetListingQuerySchema = z.object({
  version: z.nativeEnum(ListingVersion).optional(),
  language: z.nativeEnum(LanguageCode).optional(),
});

export const PublicGetListingEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ listingId, query }: { listingId: string; query: z.input<typeof PublicGetListingQuerySchema> }) =>
    `/v1/listings/${listingId}?${buildQueryString(query)}`,
  requestSchema: z.void(),
  responseSchema: PublicDetailedListingSchema.strict(),
  querySchema: PublicGetListingQuerySchema,
});

export type PublicListingV1 = z.infer<typeof PublicDetailedListingSchema>;

export const PublicGetListingIdForExperienceOrCollectionEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ exchangeId }: { exchangeId: string }) => `/v1/listings/exchange-id/${exchangeId}`,
  requestSchema: z.void(),
  responseSchema: z.string(),
});

export enum ListingStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  PUBLISHED = 'published',
  UNPUBLISHED = 'unpublished',
  ARCHIVED = 'archived',
}

export const InternalListingResourceGroupSchema = ResourceGroupSchema.pick({
  id: true,
  title: true,
  order: true,
  firstAvailableDate: true,
  paymentMethods: true,
}).extend({
  experienceIds: z.array(z.string().uuid()),
  coverPicture: ExperienceImageSizeMediaSchema.nullable(),
  pictures: z.array(ExperienceImageSizeMediaSchema).nullable().optional(),
});

export type ListingResourceGroup = z.infer<typeof InternalListingResourceGroupSchema>;

export const InternalListingSchema = ListingSchema.extend({
  status: z.nativeEnum(ListingStatus),
  createdByAi: z.boolean(),
  headline: ListingSchema.shape.headline.nullable(),
  description: ListingSchema.shape.description.nullable(),
  isUnlisted: ListingSchema.shape.isUnlisted.nullable(),
  isExclusive: ListingSchema.shape.isExclusive.nullable(),
  resourceGroups: z.array(InternalListingResourceGroupSchema).nullable(),
  auction: ListingAuctionResponse.nullable().optional(),
});

export type InternalListing = z.infer<typeof InternalListingSchema>;

export enum ListingPromotedStatus {
  ALL = 'all',
  NEEDS_PROMOTER_APPROVAL = 'needs-promoter-approval',
  APPROVED = 'approved',
  DECLINED = 'declined',
}

export const InternalGetListingsQuerySchema = paginateQuerySchema({
  sortBy: ['title', 'kind', 'date', 'status', 'categoryName', 'order'] as const,
}).extend({
  filters: z
    .array(
      z.union([
        z.object({
          operator: z.literal('in'),
          name: z.literal('hostId'),
          value: z.array(z.string().uuid()),
        }),
        z.object({
          operator: z.literal('in'),
          name: z.literal('categoryId'),
          value: z.array(z.string().uuid()),
        }),
        z.object({
          operator: z.literal('in'),
          name: z.literal('kind'),
          value: z.array(z.nativeEnum(ListingKind)),
        }),
        z.object({
          operator: z.literal('in'),
          name: z.literal('productLine'),
          value: z.array(z.nativeEnum(ProductLine)),
        }),
        z.object({
          operator: z.literal('in'),
          name: z.literal('location'),
          value: z.array(z.string()),
        }),
        z.object({
          operator: z.enum(['equals', 'greater_than', 'less_than']),
          name: z.literal('price'),
          value: z.coerce.number(),
        }),
        z.object({
          operator: z.literal('range'),
          name: z.literal('price'),
          value: z.object({ from: z.coerce.number(), to: z.coerce.number() }),
        }),
        z.object({
          operator: z.literal('equals'),
          name: z.literal('isBookable'),
          value: BooleanStringSchema,
        }),
        z.object({
          operator: z.literal('equals'),
          name: z.literal('paymentMethod'),
          value: z.nativeEnum(PaymentMethods),
        }),
        z.object({
          operator: z.literal('equals'),
          name: z.literal('isExclusive'),
          value: BooleanStringSchema,
        }),
        z.object({
          operator: z.literal('in'),
          name: z.literal('status'),
          value: z.array(z.nativeEnum(ListingStatus)),
        }),
        z.object({
          operator: z.literal('equals'),
          name: z.literal('createdByAi'),
          value: BooleanStringSchema,
        }),
        z.object({
          operator: z.literal('equals'),
          name: z.literal('isUnlisted'),
          value: BooleanStringSchema,
        }),
      ]),
    )
    .optional(),
  brandId: z.string().uuid().optional(),
  organizationId: z.string().uuid().optional(),
  listingId: z.string().uuid().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  featured: z.literal('true').optional(),
  includePromotedListings: z.literal('true').optional(),
  promotedStatus: z.nativeEnum(ListingPromotedStatus).optional(),
  language: z.nativeEnum(LanguageCode).optional(),
  version: z.nativeEnum(ListingVersion).optional(),
});

export const InternalGetListingsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ query }: { query: z.input<typeof InternalGetListingsQuerySchema> }) => `/v1/listings?${buildQueryString(query)}`,
  requestSchema: z.void(),
  responseSchema: paginateResponseSchema(InternalListingSchema.strict()),
  querySchema: InternalGetListingsQuerySchema,
});

const OrderListingsRequestSchema = z.object({
  listings: z.array(z.object({ id: z.string().uuid(), kind: z.nativeEnum(ListingKind) })),
});

export const OrderListingsEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId }: { brandId: string }) => `/v1/brands/${brandId}/order-listings`,
  requestSchema: OrderListingsRequestSchema,
  responseSchema: z.void(),
});

const InternalGetListingQuerySchema = z.object({
  version: z.nativeEnum(ListingVersion).optional(),
  brandId: z.string().uuid().optional(),
  organizationId: z.string().uuid().optional(),
  language: z.nativeEnum(LanguageCode).optional(),
});

export const InternalGetDetailedListingSchema = InternalListingSchema.omit({
  resourceGroups: true,
}).extend({
  resourceGroups: z
    .array(
      InternalListingResourceGroupSchema.extend({
        addOns: z.array(ResponseListingAddOnSchema).optional(),
        shouldValidateComplimentaryBooking: z
          .union([z.boolean(), ShouldValidateComplimentaryBookingSchema])
          .nullable()
          .optional(),
      }),
    )
    .nullable(),
  addOns: z.array(ResponseListingAddOnSchema).optional(),
  auction: ListingAuctionResponse.nullable().optional(),
  shouldValidateComplimentaryBooking: z.union([z.boolean(), ShouldValidateComplimentaryBookingSchema]).nullable().optional(),
});

export type InternalDetailedListing = z.infer<typeof InternalGetDetailedListingSchema>;

export const InternalGetListingEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ listingId, query }: { listingId: string; query: z.input<typeof InternalGetListingQuerySchema> }) =>
    `/v1/listings/${listingId}?${buildQueryString(query)}`,
  requestSchema: z.void(),
  responseSchema: InternalGetDetailedListingSchema.strict(),
  querySchema: InternalGetListingQuerySchema,
});

export const InternalGetListingIdForExperienceOrCollectionEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ exchangeId }: { exchangeId: string }) => `/v1/listings/exchange-id/${exchangeId}`,
  requestSchema: z.void(),
  responseSchema: z.string(),
});

export const InternalGetPromotedListingDetailsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, listingId }: { brandId: string; listingId: string }) =>
    `/v1/brands/${brandId}/listings/${listingId}/promoted-details`,
  requestSchema: z.void(),
  responseSchema: z.object({
    data: z.object({
      category: z.object({ id: z.string(), name: z.string() }).nullable(),
      vibes: z.array(z.object({ id: z.string(), name: z.string() })),
      overrides: z.record(z.unknown()).nullable(),
    }),
  }),
});

export const InternalPutListingOverridesEndpoint = makeEndpoint({
  method: 'PUT',
  url: ({ brandId, listingId }: { brandId: string; listingId: string }) =>
    `/v1/brands/${brandId}/listings/${listingId}/overrides`,
  requestSchema: z.object({
    overrides: z.record(
      z
        .string()
        .regex(/^(experience|resourceGroup)\.[^.]+\.(paymentMethods|integrationMeta|shouldValidateComplimentaryBooking)$/),
      z.unknown().nullable(),
    ),
  }),
  responseSchema: z.void(),
});

export const UpdateFeaturedListings = makeEndpoint({
  method: 'POST',
  url: ({ brandId }: { brandId: string }) => `/v1/brands/${brandId}/featured-listings`,
  requestSchema: z.object({ listings: z.array(z.object({ id: z.string().uuid(), order: z.number().int().nonnegative() })) }),
  responseSchema: z.void(),
});

export const GetOrganizationListingCustomFieldValuesSchema = z.object({
  customFieldId: z.string().uuid(),
  organizationId: z.string().uuid(),
  organizationName: z.string(),
  isRequired: z.boolean(),
  isDashboardEditable: z.boolean(),
  fieldName: z.string(),
  helpText: z.string().nullable(),
  fieldType: z.string(),
  enumValues: z.array(z.string()),
  isMultiSelect: z.boolean(),
  defaultValues: z.array(z.string()),
  currentValues: z.array(z.string()),
});

export const GetOrganizationListingCustomFieldValuesEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, listingId }: { brandId: string; listingId: string }) =>
    `v1/brands/${brandId}/listings/${listingId}/organization-custom-fields`,
  responseSchema: z.object({ fields: z.array(GetOrganizationListingCustomFieldValuesSchema) }).strict(),
  requestSchema: z.void(),
});

// organization custom fields
// post to custom field values from listing
const PutOrganizationCustomFieldValuesRequestSchema = z.object({
  fields: z.array(
    z.object({
      customFieldId: z.string().uuid(),
      fieldType: z.string(),
      isMultiSelect: z.boolean(),
      fieldValues: z.array(z.string()),
    }),
  ),
});

export const PutOrganizationCustomFieldValuesEndpoint = makeEndpoint({
  method: 'PUT',
  url: ({ brandId, listingId }: { brandId: string; listingId: string }) =>
    `/v1/brands/${brandId}/listings/${listingId}/organization-custom-fields`,
  requestSchema: PutOrganizationCustomFieldValuesRequestSchema,
  responseSchema: z.void(),
});

const customField = z.object({
  externalId: z.string().nullable().optional(),
  name: z.string(),
  values: z.array(z.string().nullable()),
});

// Member Benefits resolution surfaced per listing when an eligible member token is present.
// Best-% rule wins; discountedPrice is computed from the listing's starting price.
export const MemberBenefitsResponseSchema = z.object({
  isApplicable: z.boolean(),
  memberBenefitGroup: z
    .object({
      id: z.string().uuid(),
      type: z.string(),
      name: z.string(),
    })
    .nullable(),
  memberBenefitDiscountRule: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      discountPercent: z.number(),
    })
    .nullable(),
  discountedPrice: z.number().nullable(),
});

export type MemberBenefitsResponse = z.infer<typeof MemberBenefitsResponseSchema>;

export const GetListingsV2ItemSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string(),
  summary: z.string().nullable(),
  experienceId: z.string().uuid().nullable(),
  resourceGroupCollectionId: z.string().uuid().nullable(),
  kind: z.nativeEnum(ListingKind),
  productLine: z.nativeEnum(ProductLine),
  startingPrice: z.number().nullable(),
  advertisedPrice: z.number().nullable().optional(),
  pricePerType: z.string().nullable(),
  minParticipants: z.number().nullable(),
  maxParticipants: z.number().nullable(),
  coverMedia: MediaSchema.omit({ order: true }).required({ kind: true }),
  brandId: z.string().uuid().nullable(),
  brandCurrency: z.string().nullable(),
  externalBrandReferenceId: z.string().nullable().optional(),
  location: z.object({
    name: z.string(),
    latitude: z.number(),
    longitude: z.number(),
    timezoneId: z.string(),
    additionalInfo: z.string().nullable(),
  }),
  slug: z.string().nullable(),
  customFields: z.array(customField),
  rating: z.number().nullable(),
  categoryName: z.string().nullable(),
  dates: z.array(z.date()),
  hidePrice: z.boolean().nullable(),
  isUnlisted: z.boolean().nullable(),
  category: z.object({ id: z.string(), name: z.string() }).nullable(),
  vibes: z.array(z.object({ id: z.string(), name: z.string() })),
  bookingAvailabilityMode: z.nativeEnum(BookingAvailabilityModeValue).nullable(),
  isExclusive: z.boolean(),
  medias: z.array(MediaSchema.omit({ order: true }).required({ kind: true })),
  hostedById: z.string().nullable(),
  featuredOrder: z.number().nullable(),
  order: z.number().nullable(),
  createdAt: z.string().datetime().nullable(),
  publishedAt: z.string().datetime().nullable(),
  firstAvailableDate: z.date().nullable(),
  firstAvailableSessionStartTime: z.string().nullable().optional(),
  auction: ListingAuctionResponse.nullable().optional(),
  limitedTime: LimitedTimeSchema.omit({ createdAt: true }).nullable().optional(),
  customCallToAction: z.string().nullable(),
  pointsEligible: z
    .object({
      org: z.string(),
      payEligible: z.boolean(),
      earnEligible: z.boolean(),
    })
    .nullable(),
  memberBenefits: MemberBenefitsResponseSchema.nullable().optional(),
});

export type GetListingsV2Item = z.infer<typeof GetListingsV2ItemSchema>;

export const GetListingV2ResponseSchema = z.object({
  id: z.string().uuid(),
  brandId: z.string().uuid().nullable(),
  brandCurrency: z.string().nullable().optional(),
  advertisedPrice: z.number().nullable().optional(),
  externalBrandReferenceId: z.string().nullable().optional(),
  categoryId: z.string().uuid().nullable(),
  locationId: z.string().uuid().nullable(),
  experienceId: z.string().uuid().nullable(),
  resourceGroupCollectionId: z.string().uuid().nullable(),
  kind: z.nativeEnum(ListingKind),
  productLine: z.nativeEnum(ProductLine),
  title: z.string().nullable(),
  description: z.string().nullable(),
  startingPrice: z.number().nullable(),
  pricePerType: z.string().nullable(),
  minParticipants: z.number().nullable(),
  maxParticipants: z.number().nullable(),
  status: z.nativeEnum(ListingStatus).nullable(),
  summary: z.string().nullable(),
  mediaIds: z.array(z.string().uuid()),
  vibeIds: z.array(z.string().uuid()),
  startTimes: z.array(z.string()).nullable(),
  cancellationPolicyId: z.string().uuid().nullable(),
  reschedulePolicyId: z.string().uuid().nullable(),
  limitedTimeId: z.string().uuid().optional().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  auction: ListingAuctionResponse.nullable().optional(),
  memberBenefits: MemberBenefitsResponseSchema.nullable().optional(),
});

export const listingsV2SortByOptions = [
  'startingPrice',
  'rating',
  'order',
  'featuredOrder',
  'firstAvailableDate',
  'createdAt',
  'publishedAt',
  'timeLeft',
] as const;

export const listingsV2Filters = {
  isUnlisted: BooleanStringSchema.default('false'),
  kinds: z.array(z.nativeEnum(ListingKind)).optional(),
  productLines: z.array(z.nativeEnum(ProductLine)).optional(),
  categories: z.array(z.string()).optional(),
  participantCount: z.coerce.number().optional(),
  customFields: z.array(z.object({ externalId: z.string(), value: z.string() })).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  excludePromotedListings: z.literal('true').optional(),
  categoryIds: z.array(z.string().uuid()).optional(),
  vibeIds: z.array(z.string().uuid()).optional(),
  language: z.nativeEnum(LanguageCode).optional(),
  featured: z.literal('true').optional(),
  isLimitedTime: BooleanStringSchema.optional(),
  startingPriceFrom: z.coerce.number().optional(),
  startingPriceTo: z.coerce.number().optional(),
};

const GetListingsV2QuerySchema = paginateQuerySchema({
  sortBy: listingsV2SortByOptions,
})
  .omit({ filters: true })
  .extend(listingsV2Filters);

export const GetListingsV2Endpoint = makeEndpoint({
  method: 'GET',
  url: ({ query }: { query: z.input<typeof GetListingsV2QuerySchema> }) => `v2/listings?${buildQueryString(query)}`,
  responseSchema: paginateResponseSchema(GetListingsV2ItemSchema),
  requestSchema: z.void(),
  querySchema: GetListingsV2QuerySchema,
});

export const GetListingV2Endpoint = makeEndpoint({
  method: 'GET',
  url: ({ listingId }: { listingId: string }) => `v2/listings/${listingId}`,
  responseSchema: GetListingV2ResponseSchema,
  requestSchema: z.void(),
});

export enum RecommendationsTriggeredBy {
  LINKOUT = 'link-out',
  UPSELL = 'upsell',
  DEAD_LINK = 'dead-link',
}

export enum ExperienceRecommendationStatus {
  STARTED = 'started',
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export const GenerateExperienceRecommendationEndpoint = makeEndpoint({
  method: 'POST',
  url: () => `/v2/listings/recommendations/generate`,
  responseSchema: z.object({
    recommendationId: z.string().uuid(),
  }),
  requestSchema: z.object({
    brandId: z.string().uuid(),
    experienceIds: z.array(z.string().uuid()),
    // Upsell passes the booked dates. Dead-link recovery omits them; the API uses the property's next 5 days.
    from: z.string().optional(),
    language: z.nativeEnum(LanguageCode).default(LanguageCode.EN_US).optional(),
    resourceGroupCollectionIds: z.array(z.string().uuid()),
    to: z.string().optional(),
    triggeredBy: z.nativeEnum(RecommendationsTriggeredBy).default(RecommendationsTriggeredBy.UPSELL).optional(),
    // Dead-link recovery only: the id or slug from the retired experience URL the guest tried to open.
    sourceExperienceSlug: z.string().min(1).max(255).optional(),
  }),
});

export type GenerateExperienceRecommendationRequestBody = z.infer<
  typeof GenerateExperienceRecommendationEndpoint.requestSchema
>;

export const RecommendationItemSchema = z.object({
  listingId: z.string().uuid(),
  experienceId: z.string().uuid().nullable(),
  rank: z.number().int().min(1).max(3),
  reason: z.string().max(200),
});

export type RecommendationItem = z.infer<typeof RecommendationItemSchema>;

export const GetExperienceRecommendationEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ recommendationId, language }: { recommendationId: string; language?: LanguageCode }) =>
    `/v2/listings/recommendations/${recommendationId}?${buildQueryString({ language: language ?? LanguageCode.EN_US })}`,
  responseSchema: z.object({
    listings: z.array(GetListingsV2ItemSchema),
    status: z.nativeEnum(ExperienceRecommendationStatus),
  }),
  requestSchema: z.void(),
});

export enum ListingField {
  TITLE = 'title',
  DESCRIPTION = 'description',
  SUMMARY = 'summary',
  HEADLINE = 'headline',
}

const FeaturedListSchema = z.object({
  listingBrandId: z.string().uuid(),
  listingId: z.string().uuid(),
  listingTitle: z.string(),
  order: z.number().int().nonnegative(),
});

const GetFeaturedListResponseSchema = z.array(FeaturedListSchema);

export const GetFeaturedListEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `/v1/featured-list/brands/${brandId}`,
  responseSchema: GetFeaturedListResponseSchema,
  requestSchema: z.void(),
});

const FavoriteListSchema = z.object({
  listingUserId: z.string().uuid(),
  listingId: z.string().uuid(),
  order: z.coerce.number().int().nonnegative(),
  title: z.string().nullable(),
  brandId: z.string().uuid().nullable(),
  experienceId: z.string().uuid().nullable(),
  resourceGroupCollectionId: z.string().uuid().nullable(),
});

const GetFavoriteListResponseSchema = z.array(FavoriteListSchema);

export const GetFavoriteListEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ userId, brandId }: { userId: string; brandId: string }) => `/v1/favorite-list/users/${userId}/${brandId}`,
  responseSchema: GetFavoriteListResponseSchema,
  requestSchema: z.void(),
});

const SetFeaturedListRequestSchema = z.array(
  z.object({
    listingId: z.string().uuid(),
    order: z.number().int().nonnegative(),
  }),
);

const AddRemoveFeaturedListResponseSchema = z.object({
  totalFeaturedCount: z.number().int().nonnegative(),
});

export const SetFeaturedListEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId }: { brandId: string }) => `/v1/featured-list/brands/${brandId}`,
  responseSchema: AddRemoveFeaturedListResponseSchema,
  requestSchema: SetFeaturedListRequestSchema,
});

const SetFavoriteListRequestSchema = z.array(
  z.object({
    listingId: z.string().uuid(),
    order: z.number().int().nonnegative(),
  }),
);

const AddRemoveFavoriteListResponseSchema = z.object({
  totalFavoriteCount: z.number().int().nonnegative(),
});

export const SetFavoriteListEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ userId }: { userId: string }) => `/v1/favorite-list/users/${userId}`,
  responseSchema: AddRemoveFavoriteListResponseSchema,
  requestSchema: SetFavoriteListRequestSchema,
});

export const AddToFeaturedListEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ brandId, listingId }: { brandId: string; listingId: string }) =>
    `/v1/featured-list/brands/${brandId}/listings/${listingId}`,
  responseSchema: AddRemoveFeaturedListResponseSchema,
  requestSchema: z.void(),
});

export const AddToFavoriteListEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ userId, listingId }: { userId: string; listingId: string }) =>
    `/v1/favorite-list/users/${userId}/listings/${listingId}`,
  responseSchema: AddRemoveFavoriteListResponseSchema,
  requestSchema: z.void(),
});

export const DeleteFromFeaturedListEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ brandId, listingId }: { brandId: string; listingId: string }) =>
    `/v1/featured-list/brands/${brandId}/listings/${listingId}`,
  responseSchema: AddRemoveFeaturedListResponseSchema,
  requestSchema: z.void(),
});

export const DeleteFromFavoriteListEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ userId, listingId }: { userId: string; listingId: string }) =>
    `/v1/favorite-list/users/${userId}/listings/${listingId}`,
  responseSchema: AddRemoveFavoriteListResponseSchema,
  requestSchema: z.void(),
});

export const GetListingTranslationsQuerySchema = z.object({
  language: z.nativeEnum(LanguageCode).optional(),
});

export const GetListingTranslationsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ listingId, query }: { listingId: string; query: z.input<typeof GetListingTranslationsQuerySchema> }) =>
    `/v1/listings/${listingId}/i18n?${buildQueryString(query)}`,
  responseSchema: z.object({
    translations: z.array(TranslationSchema),
  }),
  requestSchema: z.void(),
  querySchema: GetListingTranslationsQuerySchema,
});

export const PatchListingTranslationsRequestSchema = z.object({
  translations: z.array(TranslationSchema.omit({ createdAt: true, updatedAt: true })),
});

export const PatchListingTranslationsEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ listingId }: { listingId: string }) => `/v1/listings/${listingId}/i18n`,
  responseSchema: z.void(),
  requestSchema: PatchListingTranslationsRequestSchema,
});

const PATCH_LISTING_V3_OVERRIDE_KEY_REGEX =
  /^(experience|resourceGroup)\.[^.]+\.(paymentMethods|integrationMeta|shouldValidateComplimentaryBooking)$/;

export const PatchListingV3RequestSchema = z
  .object({
    title: z.string().nullable().optional(),
    summary: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    categoryId: z.string().uuid().nullable().optional(),
    vibeIds: z.array(z.string().uuid()).optional(),
    overrides: z.record(z.string().regex(PATCH_LISTING_V3_OVERRIDE_KEY_REGEX), z.unknown().nullable()).optional(),
  })
  .refine(
    (data) =>
      data.title !== undefined ||
      data.summary !== undefined ||
      data.description !== undefined ||
      data.categoryId !== undefined ||
      data.vibeIds !== undefined ||
      data.overrides !== undefined,
    { message: 'At least one field must be provided' },
  );

export const PatchListingV3Endpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ brandId, listingId }: { brandId: string; listingId: string }) => `/v3/brands/${brandId}/listings/${listingId}`,
  responseSchema: z.void(),
  requestSchema: PatchListingV3RequestSchema,
});

export const GetListingCountByPromoterCustomFieldResponseSchema = z.object({
  items: z.array(
    z.object({
      fieldValues: z.array(z.string()),
      count: z.number(),
    }),
  ),
  totalItems: z.number(),
});

export const GetListingCountByPromoterCustomFieldEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ promoterBrandId, externalId }: { promoterBrandId: string; externalId: string }) =>
    `/v1/promoters/${promoterBrandId}/promoter-custom-fields/count-by-custom-field/${externalId}`,
  requestSchema: z.void(),
  responseSchema: GetListingCountByPromoterCustomFieldResponseSchema,
});

export const GetListingCountByOrganizationCustomFieldResponseSchema = z.object({
  items: z.array(
    z.object({
      value: z.string(),
      count: z.number(),
    }),
  ),
  totalItems: z.number(),
});

export const GetListingCountByOrganizationCustomFieldEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, externalId }: { brandId: string; externalId: string }) =>
    `/v1/promoters/${brandId}/organization-custom-fields/count-by-custom-field/${externalId}`,
  requestSchema: z.void(),
  responseSchema: GetListingCountByOrganizationCustomFieldResponseSchema,
});
